<script setup lang="ts">
import { computed } from 'vue';
import { Feature } from '@/api/tracking';
import { EMPTY_KEYWORD_FEED_MESSAGE } from '@/constants';
import { DgrLoading } from '@stockmarkteam/donguri-ui';
import { Dayjs } from 'dayjs';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import BasicEmptyFeed from '@/components/common/basic-empty-feed.vue';
import ThemeEmptyFeed from '@/components/common/theme-empty-feed.vue';
import {
  Article,
  ArticleMarkSource,
  FeedData,
  FeedType,
  Theme,
  ThemeFeedType,
} from '@/types';
import { articleHideable, themeEditable } from '@/utils/authorization';
import {
  OptionMenuConfig,
  useAdpDocumentOptionMenu,
} from '@/utils/composables/useAdpDocumentOptionMenu';
import { useThemeDisabledOption } from '@/utils/composables/useThemeDisabledOption';
import { useGroups, useUserInfo } from '@/utils/swr';

const props = withDefaults(
  defineProps<{
    articleCountBeforeDate?: number;
    articles?: Article[];
    articleCount?: number;
    theme?: Theme;
    date: Dayjs;
    dateStr: string;
    dateLabel: string;
    feedData: FeedData;
    isVisibleEmptyFeed?: boolean;
    feed: 'keyword_feed' | 'related_feed' | 'personal_news';
    displayFeedType: FeedType;
    themeFeedType: ThemeFeedType;
  }>(),
  {
    articleCountBeforeDate: 0,
    articles: () => [],
    articleCount: 0,
    isVisibleEmptyFeed: false,
  },
);

const rankInWholeFeed = (article: Article): number | undefined => {
  if (!article.rank) {
    return undefined;
  }
  // 対象記事の前日のフィードまでの合計記事数を計算し、それに記事のrankを足す
  return props.articleCountBeforeDate > 0
    ? props.articleCountBeforeDate + article.rank
    : article.rank;
};

const markSource: ArticleMarkSource =
  props.feed === 'personal_news' ? 'personal_feed' : 'theme';
const feature: Feature =
  props.feed === 'personal_news' ? 'personal_feed' : props.feed;

const { data: userInfo } = useUserInfo();
const { data: groups } = useGroups();

const enableToEditTheme = computed(
  () =>
    userInfo.value?.role !== 'viewer' ||
    (props.theme?.access_scope === 'personal' &&
      props.theme?.user_id === userInfo.value?.id),
);

// 記事を非表示にすることができる
const isArticleHideable = computed(() => {
  const groupId = props.theme?.group_id;
  const themeGroup = groups.value?.groups?.find(x => x.id === groupId);
  return articleHideable(userInfo.value, props.theme, themeGroup);
});

// 記事のメディアを除外することができる
const isSiteExcludable = computed(() => {
  const groupId = props.theme?.group_id;
  const themeGroup = groups.value?.groups?.find(x => x.id === groupId);
  return themeEditable(userInfo.value, props.theme, themeGroup);
});

const { disabledOption } = props.theme
  ? useThemeDisabledOption({
      theme: props.theme,
      themeFeedType: props.themeFeedType,
      docType: 'article',
    })
  : { disabledOption: undefined };

const isPersonalFeed = props.feed === 'personal_news';
const optionMenu = useAdpDocumentOptionMenu({
  pageName: props.feed,
  feature,
  themeId: () => props.theme?.id,
  themeExcludedSitesCount: () => props.theme?.site_exclusion_ids.length,
  feedType: () => props.themeFeedType,
  enablePersonalFeedSiteExclusion: isPersonalFeed,
});
const menuConfig = computed<OptionMenuConfig>(() =>
  isPersonalFeed
    ? { notInterested: true, excludeMedia: true }
    : {
        undisplay: !!props.theme && isArticleHideable.value,
        siteExclusion:
          !!props.theme && isSiteExcludable.value && !props.theme.is_chinese,
      },
);
</script>

<template>
  <div class="feed-articles-media">
    <div class="media-title c-title-bold--m">
      {{ themeFeedType === 'domestic' ? '国内メディア' : '海外メディア' }}
      <span v-if="!disabledOption" class="media-title-count c-text-regular--s">
        ・{{ articleCount }}件
      </span>
    </div>
    <div class="article-card" v-for="article in articles" :key="article.id">
      <div class="divider"></div>
      <AdpDocumentCard
        width="100%"
        :adp-document="article"
        :theme-id="theme ? theme.id : undefined"
        :feed-type="themeFeedType"
        :rank-in-whole-feed="rankInWholeFeed(article)"
        :show-comment-count="1"
        :mark-source="markSource"
        :page-name="feed"
        :feature="feature"
        :disable-related-articles="false"
        :option-actions="optionMenu.buildOptionActions(article, menuConfig)"
        @option-action="
          name =>
            optionMenu.handleOptionAction(
              name,
              article,
              rankInWholeFeed(article),
            )
        "
      ></AdpDocumentCard>
    </div>
    <div style="display: flex; justify-content: center">
      <button
        v-if="feedData.isViewMoreButtonVisible"
        class="view-more-button c-text-regular--m c-btn c-btn--auto c-btn--small c-btn--outline"
        @click="() => feedData.loadMore()"
      >
        すべて表示（{{ feedData.remainingArticles }}）
      </button>
    </div>
    <div v-if="feedData.isViewMoreButtonVisible" class="spacing-16" />
    <DgrLoading
      v-if="!feedData.isLoaded || feedData.isFetchingData"
      class="loading loading-spacer"
    />
    <template v-if="isVisibleEmptyFeed">
      <div class="divider"></div>
      <ThemeEmptyFeed
        v-if="theme"
        :display-feed-type="displayFeedType"
        :theme-feed-type="themeFeedType"
        doc-type="article"
        :theme="theme"
        :enable-to-edit-theme="enableToEditTheme"
        >{{
          feed === 'keyword_feed'
            ? EMPTY_KEYWORD_FEED_MESSAGE
            : '関連ニュースはありませんでした。'
        }}
      </ThemeEmptyFeed>
      <BasicEmptyFeed v-else>{{ EMPTY_KEYWORD_FEED_MESSAGE }}</BasicEmptyFeed>
    </template>
  </div>
</template>

<style scoped lang="scss">
.feed-articles-media {
  position: relative;
  background: #fff;
  border-radius: 4px;
}

.media-title {
  padding: 10px 0 0 16px;
  margin-bottom: 16px;
}
.media-title-count {
  color: $color-gray600;
}

.article-card {
  margin-bottom: 8px;
  .document-card {
    border: 0;
  }
}

.divider {
  width: auto;
  border-bottom: 0.5px solid $color-gray400;
  margin: 0px 16px 0px 16px;
}

.view-more-button {
  display: inline-flex;
  align-self: center;
  padding: 4px 12px;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.loading {
  align-self: center;
}
.loading-spacer {
  padding-bottom: 80vh;
}
</style>
